import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import { CopyToClipboardButton } from "@/components/CopyToClipboardButton";
import { TournamentEditDialog } from "@/components/dialogs/TournamentEditDialog";
import { TournamentStageCreateDialog } from "@/components/dialogs/TournamentStageCreateDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { FragmentType, getFragmentData } from "@/gen";
import {
    TournamentStageT,
    TournamentStageTable_TournamentStage,
    TournamentT,
} from "@/routes/tournament/queries";

export default function TournamentRoute() {
    const { tournament } = useOutletContext<{
        tournament: TournamentT;
    }>();

    return (
        <Card className="bg-brand-white text-brand-black m-4 rounded border-none">
            <CardHeader>
                <CardTitle>{tournament.title}</CardTitle>
                <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                        <div className="flex-row items-center">
                            <span className="text-sm">ID: {tournament.id}</span>
                            <CopyToClipboardButton text={tournament.id} />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button asChild variant="outline">
                            <Link to="layouts">Manage Layouts</Link>
                        </Button>
                        <TournamentEditDialog />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <TournamentStageTable tournamentStagesFragment={tournament.stages} />
            </CardContent>
        </Card>
    );
}

const columns: ColumnDef<TournamentStageT>[] = [
    {
        accessorKey: "title",
        header: () => <div>Title</div>,
        cell: ({ row }) => {
            return <div className="font-medium">{row.original.title}</div>;
        },
    },
    {
        accessorKey: "kind",
        header: () => <div>Kind</div>,
        cell: ({ row }) => {
            return <div>{row.original.kind}</div>;
        },
    },
    {
        accessorKey: "order",
        header: () => <div>Order</div>,
        cell: ({ row }) => {
            return <div>{row.original.order}</div>;
        },
    },
    {
        accessorKey: "createdAt",
        header: () => <div>Created at</div>,
        cell: ({ row }) => {
            return <div>{format(row.original.createdAt, "PPpp")}</div>;
        },
    },
];

const TournamentStageTable = ({
    tournamentStagesFragment,
}: {
    tournamentStagesFragment: FragmentType<typeof TournamentStageTable_TournamentStage>[];
}) => {
    const navigate = useNavigate();

    const tournamentStages = getFragmentData(
        TournamentStageTable_TournamentStage,
        tournamentStagesFragment
    );

    return (
        <div>
            <DataTable
                columns={columns}
                data={tournamentStages}
                onRowClick={row => {
                    navigate(`stages/${row.id}`);
                }}
                title="Tournament stages"
                addButton={<TournamentStageCreateDialog />}
            />
        </div>
    );
};
